import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputBase,
  MenuItem,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../global/theme/tokens';
import ProjectAvatar from '../exo/project/ProjectAvatar';

/**
 * Project picker, built like EmployeesMultiSelect so the dropdowns match.
 * Used by the pool to narrow the free tasks to single projects.
 */
const ProjectsMultiSelect = ({ projects = [], selectedIds = [], onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);

  // a company has far too many projects for plain scrolling
  const [search, setSearch] = useState('');

  const term = search.trim().toLowerCase();

  const visibleProjects = term
    ? projects.filter((project) =>
        [project.name, project.nr]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term)),
      )
    : projects;

  function closePopover() {
    setAnchorEl(null);
    setSearch('');
  }

  function toggleProject(projectId) {
    onChange(
      selectedIds.includes(projectId)
        ? selectedIds.filter((id) => id !== projectId)
        : [...selectedIds, projectId],
    );
  }

  return (
    <Box>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="flex items-center gap-2"
        sx={{
          borderRadius: '8px',
          paddingY: '4px',
          color: 'inherit',
          textTransform: 'none',
          backgroundColor: colors.bgInput,
          ':hover': { backgroundColor: `${colors.info}20` },
        }}
      >
        <AccountTreeIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
        <Typography fontSize="12px" className="opacity-60">
          {t('Project')}
        </Typography>
        <Typography fontSize="12px" fontWeight={500} noWrap>
          {selectedIds.length ? selectedIds.length : t('All')}
        </Typography>

        {/* a div, not a button - the trigger around it is one already */}
        {selectedIds.length > 0 && (
          <Box
            component="span"
            className="flex items-center rounded-full"
            title={t('Clear selection')}
            onClick={(e) => {
              // the picker must not open on the way out
              e.stopPropagation();
              onChange([]);
            }}
            sx={{
              padding: '2px',
              ':hover': { backgroundColor: `${colors.text}20` },
            }}
          >
            <CloseIcon sx={{ fontSize: '14px' }} />
          </Box>
        )}

        <KeyboardArrowDownIcon
          sx={{
            fontSize: '18px',
            transform: `rotate(${anchorEl ? 180 : 0}deg)`,
            transition: 'transform 200ms',
          }}
        />
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // the global backdrop blurs everything behind any modal
        sx={{
          '.MuiBackdrop-root': {
            bgcolor: 'transparent',
            background: 'unset',
            backdropFilter: 'unset',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '290px',
              // the paper sizes to its content, so a long project name would
              // stretch it across the page - it is cut off instead
              maxWidth: 'min(340px, calc(100vw - 32px))',
              maxHeight: '360px',
              borderRadius: '12px',
              padding: '4px',
              backgroundColor: colors.glassSolid,
              backgroundImage: 'unset',
              border: `1px solid ${colors.text}15`,
              // the global list styles put a border on every item
              '& .MuiMenuItem-root': {
                border: 'none !important',
                borderRadius: '8px',
                gap: 1,
                paddingY: '4px',
                // without this an item keeps the width of its own label
                maxWidth: '100%',
              },
            },
          },
        }}
      >
        {/* the search stays in place while the list below it scrolls */}
        <Box
          className="sticky top-0 z-10 pb-1"
          // the paper pads itself, which an item would otherwise scroll through
          sx={{
            backgroundColor: colors.glassSolid,
            marginTop: '-4px',
            paddingTop: '4px',
          }}
        >
          <Box
            className="flex items-center gap-1 rounded-lg px-2"
            sx={{
              backgroundColor: colors.bgInput,
              border: `1px solid ${colors.text}20`,
              ':focus-within': { borderColor: `${colors.info}80` },
            }}
          >
            <SearchIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
            <InputBase
              autoFocus
              value={search}
              placeholder={t('Search project')}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && search) {
                  // the first escape clears, only the second closes the picker
                  e.stopPropagation();
                  setSearch('');
                }
              }}
              sx={{ fontSize: '13px', width: '100%', paddingY: '2px' }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch('')}>
                <CloseIcon sx={{ fontSize: '15px' }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {visibleProjects.map((project) => (
          <MenuItem key={project.id} onClick={() => toggleProject(project.id)}>
            <Checkbox
              size="small"
              checked={selectedIds.includes(project.id)}
              sx={{ padding: '4px' }}
            />
            <ProjectAvatar project={project} size="22px" />
            <Box className="flex flex-col grow min-w-0">
              {/* cut off names are still readable on hover */}
              <Typography
                fontSize="13px"
                fontWeight={500}
                noWrap
                title={project.name}
              >
                {project.name}
              </Typography>
              {project.nr && (
                <Typography fontSize="11px" className="monospace opacity-60">
                  {project.nr}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}

        {!visibleProjects.length && (
          <Typography fontSize="13px" className="opacity-50 px-3 py-2">
            {term ? t('No projects match the search') : t('No projects found')}
          </Typography>
        )}
      </Popover>
    </Box>
  );
};

export default ProjectsMultiSelect;
